import type {
  GoogleNexusConnectionResult,
  GoogleNexusDashboard,
  GoogleNexusUpload,
  GoogleNexusProjectInput,
  GoogleNexusRefreshResult,
  GoogleNexusSyncResult,
  GoogleNexusValidationInput,
  GoogleNexusValidationResult,
  NexusBugDetail,
  NexusBugEditor,
  NexusBugInput,
  NexusBugList,
  NexusBugMetadata,
  NexusBugCostInput,
  NexusBugEstimateInput,
  NexusBugReportResult,
  NexusCsvExport,
  NexusDeliveredTasksQuery,
  NexusDeliveredTasksResult,
  NexusDeliveryOverview,
  NexusExportScope,
  NexusProjectDetail,
  StagedGoogleNexusArchive,
} from './model';

export interface NexusGateway {
  fetchDashboard: () => Promise<GoogleNexusDashboard>;
  fetchUpload: (uploadId: string) => Promise<{ upload: GoogleNexusUpload | null }>;
  saveProject: (project: GoogleNexusProjectInput) => Promise<{ id: string } & GoogleNexusProjectInput>;
  setProjectArchived: (projectId: string, archived: boolean) => Promise<{ projectId: string; archived: boolean }>;
  testConnection: (projectId: string) => Promise<GoogleNexusConnectionResult>;
  syncProject: (projectId: string) => Promise<GoogleNexusSyncResult>;
  stageArchive: (
    uploadId: string,
    archive: File,
    onProgress?: (bytesTransferred: number, totalBytes: number) => void,
  ) => Promise<StagedGoogleNexusArchive>;
  submitValidation: (submission: GoogleNexusValidationInput) => Promise<GoogleNexusValidationResult>;
  refreshUpload: (uploadId: string) => Promise<GoogleNexusRefreshResult>;
  refreshActiveUploads: () => Promise<{ refreshed: number; results: GoogleNexusRefreshResult[] }>;
  fetchDeliveredTasks: (query: NexusDeliveredTasksQuery) => Promise<NexusDeliveredTasksResult>;
  fetchDeliveryOverview: () => Promise<NexusDeliveryOverview>;
  fetchDeliveryBug: (bugId: string) => Promise<NexusBugDetail>;
  fetchDeliveryProject: (projectId: string) => Promise<NexusProjectDetail>;
  exportDeliveredTasks: (scope: NexusExportScope, id?: string) => Promise<NexusCsvExport>;
  fetchNexusBugs: () => Promise<NexusBugList>;
  fetchNexusBug: (bugId: string) => Promise<NexusBugEditor>;
  saveNexusBug: (bugId: string, bug: NexusBugInput) => Promise<NexusBugMetadata>;
  reportNexusBugCost: (bugId: string, report: NexusBugCostInput) => Promise<NexusBugReportResult>;
  reportNexusBugEstimate: (bugId: string, report: NexusBugEstimateInput) => Promise<NexusBugReportResult>;
}

export function createNexusApi(gateway: NexusGateway) {
  return {
    fetchDashboard: () => gateway.fetchDashboard(),
    fetchUpload: (uploadId: string) => gateway.fetchUpload(uploadId),
    saveProject: (project: GoogleNexusProjectInput) => gateway.saveProject(project),
    setProjectArchived: (projectId: string, archived: boolean) => gateway.setProjectArchived(projectId, archived),
    testConnection: (projectId: string) => gateway.testConnection(projectId),
    syncProject: (projectId: string) => gateway.syncProject(projectId),
    stageArchive: (
      uploadId: string,
      archive: File,
      onProgress?: (bytesTransferred: number, totalBytes: number) => void,
    ) => gateway.stageArchive(uploadId, archive, onProgress),
    submitValidation: (submission: GoogleNexusValidationInput) => gateway.submitValidation(submission),
    refreshUpload: (uploadId: string) => gateway.refreshUpload(uploadId),
    refreshActiveUploads: () => gateway.refreshActiveUploads(),
    fetchDeliveredTasks: (query: NexusDeliveredTasksQuery) => gateway.fetchDeliveredTasks(query),
    fetchDeliveryOverview: () => gateway.fetchDeliveryOverview(),
    fetchDeliveryBug: (bugId: string) => gateway.fetchDeliveryBug(bugId),
    fetchDeliveryProject: (projectId: string) => gateway.fetchDeliveryProject(projectId),
    exportDeliveredTasks: (scope: NexusExportScope, id?: string) => gateway.exportDeliveredTasks(scope, id),
    fetchNexusBugs: () => gateway.fetchNexusBugs(),
    fetchNexusBug: (bugId: string) => gateway.fetchNexusBug(bugId),
    saveNexusBug: (bugId: string, bug: NexusBugInput) => gateway.saveNexusBug(bugId, bug),
    reportNexusBugCost: (bugId: string, report: NexusBugCostInput) => gateway.reportNexusBugCost(bugId, report),
    reportNexusBugEstimate: (bugId: string, report: NexusBugEstimateInput) => gateway.reportNexusBugEstimate(bugId, report),
  };
}

